export type Point = { lat: number; lng: number } | [number, number];

type XY = { x: number; y: number };

const toXY = (p: Point): XY => Array.isArray(p) ? { x: p[0], y: p[1] } : { x: p.lng, y: p.lat };

const onSegment = (p: XY, q: XY, r: XY) =>
  Math.min(p.x, r.x) <= q.x && q.x <= Math.max(p.x, r.x) &&
  Math.min(p.y, r.y) <= q.y && q.y <= Math.max(p.y, r.y);

const orientation = (p: XY, q: XY, r: XY) => {
  const val = (q.y - p.y) * (r.x - q.x) - (q.x - p.x) * (r.y - q.y);
  if (Math.abs(val) < 1e-12) return 0; // colinear
  return val > 0 ? 1 : 2; // 1: clockwise, 2: counterclockwise
};

export const segmentsIntersect = (a1: Point, a2: Point, b1: Point, b2: Point) => {
  const p1 = toXY(a1), q1 = toXY(a2), p2 = toXY(b1), q2 = toXY(b2);
  const o1 = orientation(p1, q1, p2);
  const o2 = orientation(p1, q1, q2);
  const o3 = orientation(p2, q2, p1);
  const o4 = orientation(p2, q2, q1);

  if (o1 !== o2 && o3 !== o4) return true;
  if (o1 === 0 && onSegment(p1, p2, q1)) return true;
  if (o2 === 0 && onSegment(p1, q2, q1)) return true;
  if (o3 === 0 && onSegment(p2, p1, q2)) return true;
  if (o4 === 0 && onSegment(p2, q1, q2)) return true;
  return false;
};

export const hasSelfIntersectionsLine = (points: Point[]): boolean => {
  if (!points || points.length < 2) return false;
  for (let i = 0; i < points.length - 1; i++) {
    for (let j = i + 1; j < points.length - 1; j++) {
      // Skip adjacent segments sharing a vertex
      if (Math.abs(i - j) <= 1) continue;
      if (segmentsIntersect(points[i], points[i + 1], points[j], points[j + 1])) return true;
    }
  }
  return false;
};

export const hasSelfIntersectionsPolygon = (points: Point[]): boolean => {
  if (!points || points.length < 3) return false;
  // Build closed ring
  const ring = [...points, points[0]];
  for (let i = 0; i < ring.length - 1; i++) {
    for (let j = i + 1; j < ring.length - 1; j++) {
      // Skip the same or adjacent edges including wrap-around
      if (Math.abs(i - j) <= 1) continue;
      if (i === 0 && j === ring.length - 2) continue; // first and last are adjacent
      if (segmentsIntersect(ring[i], ring[i + 1], ring[j], ring[j + 1])) return true;
    }
  }
  return false;
};

export const validateLine = (points: Point[]) => {
  if (!points || points.length < 2) return { ok: false, error: 'A line needs at least 2 points' };
  return { ok: true };
};

export const validatePolygon = (points: Point[]) => {
  if (!points || points.length < 3) return { ok: false, error: 'An area needs at least 3 points' };
  if (hasSelfIntersectionsPolygon(points)) return { ok: false, error: 'The area has self-intersections. Please adjust points.' };
  return { ok: true };
};
